import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { X, StickyNote, FileText, CalendarPlus, PhoneIncoming, PhoneOutgoing } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCallStore } from '@/stores/callStore'
import { useUIStore } from '@/stores/uiStore'
import { apiClient } from '@/lib/api'

const AUTO_DISMISS_MS = 30_000

// Outcome chips for call disposition. An outcome is a choice, not a phase of
// work: unselected chips are neutral glass and the selected one is white.
const OUTCOME_CHIPS = [
  { label: 'Interested',     tintBg: '#FFFFFF', tintBorder: 'rgba(255,255,255,0.12)', tintText: '#000000' },
  { label: 'Callback',       tintBg: '#FFFFFF', tintBorder: 'rgba(255,255,255,0.12)', tintText: '#000000' },
  { label: 'Not interested', tintBg: '#FFFFFF', tintBorder: 'rgba(255,255,255,0.12)', tintText: '#000000' },
  { label: 'Voicemail',      tintBg: '#FFFFFF', tintBorder: 'rgba(255,255,255,0.12)', tintText: '#000000' },
] as const

/**
 * AC-38: Post-call card. Renders as soon as a call ends, auto-dismisses
 * after 30s if untouched, offers outcome chips + Add-Note / Create-Quote /
 * Schedule-Follow-up CTAs. Mounted once at the app root.
 */
export function PostCallCard(): React.ReactElement | null {
  const lastEndedCall = useCallStore((s) => s.lastEndedCall)
  const clearLastEndedCall = useCallStore((s) => s.clearLastEndedCall)
  const addToast = useUIStore((s) => s.addToast)
  const navigate = useNavigate()

  const [showNoteForm, setShowNoteForm] = useState(false)
  const [note, setNote] = useState('')
  const [submittingNote, setSubmittingNote] = useState(false)
  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(null)

  // Auto-dismiss after 30s if untouched
  useEffect(() => {
    if (!lastEndedCall) return
    if (showNoteForm || note.length > 0) return
    const timer = setTimeout(() => {
      clearLastEndedCall()
      setShowNoteForm(false)
      setNote('')
      setSelectedOutcome(null)
    }, AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [lastEndedCall, clearLastEndedCall, showNoteForm, note])

  if (!lastEndedCall) return null

  const handleSubmitNote = async () => {
    if (!note.trim()) return
    setSubmittingNote(true)
    try {
      const payload: Record<string, unknown> = {
        type: 'NOTE',
        content: note.trim(),
      }
      if (lastEndedCall.contactId) payload.contactId = lastEndedCall.contactId
      if (lastEndedCall.callSid) payload.callSid = lastEndedCall.callSid
      if (selectedOutcome) payload.outcome = selectedOutcome
      await apiClient.post('/api/activities', payload)
      addToast({
        type: 'success',
        title: lastEndedCall.contactId ? 'Note saved to contact' : 'Note saved',
        duration: 3000,
      })
      setNote('')
      setShowNoteForm(false)
      setSelectedOutcome(null)
      clearLastEndedCall()
    } catch {
      addToast({ type: 'error', title: 'Failed to save note', duration: 4000 })
    } finally {
      setSubmittingNote(false)
    }
  }

  const handleCreateQuote = () => {
    const params = new URLSearchParams()
    if (lastEndedCall.contactId) params.set('contactId', lastEndedCall.contactId)
    if (lastEndedCall.callSid) params.set('callSid', lastEndedCall.callSid)
    navigate(`/quotes/builder${params.toString() ? `?${params.toString()}` : ''}`)
    clearLastEndedCall()
  }

  const handleScheduleFollowUp = async () => {
    const scheduledFor = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    const contactName = lastEndedCall?.contactName || lastEndedCall?.number || 'contact'
    try {
      const payload: Record<string, unknown> = {
        type: 'TASK',
        content: `Follow up with ${contactName}`,
        scheduledFor,
      }
      if (lastEndedCall?.contactId) payload.contactId = lastEndedCall.contactId
      if (lastEndedCall?.callSid) payload.callSid = lastEndedCall.callSid
      await apiClient.post('/api/activities', payload)
      addToast({ type: 'success', title: 'Follow-up task created', duration: 3000 })
      clearLastEndedCall()
    } catch {
      addToast({ type: 'error', title: 'Failed to create follow-up task', duration: 4000 })
    }
  }

  const contextInfo = buildContextInfo(lastEndedCall.context)
  const mins = Math.floor(lastEndedCall.duration / 60)
  const secs = lastEndedCall.duration % 60
  const durationLabel = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`

  return (
    <div
      className="fixed bottom-[140px] right-4 left-4 md:bottom-24 md:right-6 md:left-auto z-[55] md:w-80 animate-slide-up-sheet md:animate-fade-up"
    >
      <div className="ds-tile-solid">
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            borderBottom: '0.5px solid var(--midnight-border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {lastEndedCall.direction === 'inbound' ? (
              <PhoneIncoming style={{ width: 14, height: 14, color: 'rgba(255,255,255,0.60)' }} />
            ) : (
              <PhoneOutgoing style={{ width: 14, height: 14, color: 'rgba(255,255,255,0.60)' }} />
            )}
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
              }}
            >
              Call ended
            </span>
          </div>
          <button
            type="button"
            onClick={clearLastEndedCall}
            aria-label="Dismiss post-call card"
            style={{
              width: 28,
              height: 28,
              borderRadius: 'var(--r-pill)',
              border: '0.5px solid var(--midnight-border)',
              background: 'var(--midnight-surface-2, #14141A)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
            }}
          >
            <X style={{ width: 12, height: 12 }} />
          </button>
        </div>

        {/* Call summary */}
        <div style={{ padding: '12px 16px 8px' }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF', marginBottom: 2 }}>
            {lastEndedCall.contactName || lastEndedCall.number}
          </p>
          {lastEndedCall.contactId && lastEndedCall.contactName && (
            <button
              type="button"
              onClick={() => {
                navigate(`/customers/people/${lastEndedCall.contactId}`)
                clearLastEndedCall()
              }}
              style={{
                fontSize: 12,
                color: '#FFFFFF',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                marginBottom: 4,
                display: 'block',
              }}
            >
              View contact
            </button>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
            }}
          >
            <span>{durationLabel}</span>
            <span>·</span>
            <span style={{ textTransform: 'capitalize' }}>{lastEndedCall.direction}</span>
            {contextInfo && (
              <>
                <span>·</span>
                {contextInfo.route ? (
                  <Link
                    to={contextInfo.route}
                    onClick={clearLastEndedCall}
                    style={{ color: '#FFFFFF' }}
                  >
                    {contextInfo.label}
                  </Link>
                ) : (
                  <span>{contextInfo.label}</span>
                )}
              </>
            )}
          </div>
          {lastEndedCall.finalQuality && (
            <div style={{ fontSize: 12, color: 'var(--midnight-text2, rgba(255,255,255,0.60))', marginTop: 4 }}>
              Quality:{' '}
              <span
                style={{
                  fontWeight: 600,
                  color: lastEndedCall.finalQuality === 'good'
                    ? 'var(--midnight-mint)'
                    : lastEndedCall.finalQuality === 'fair'
                      ? 'rgba(255,255,255,0.70)'
                      : 'var(--midnight-blush)',
                }}
              >
                {lastEndedCall.finalQuality}
              </span>
            </div>
          )}
        </div>

        {/* Outcome chips */}
        {!showNoteForm && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              padding: '4px 16px 10px',
            }}
          >
            {OUTCOME_CHIPS.map((chip) => (
              <button
                key={chip.label}
                type="button"
                onClick={() => setSelectedOutcome(
                  selectedOutcome === chip.label ? null : chip.label
                )}
                style={{
                  height: 28,
                  padding: '0 10px',
                  borderRadius: 'var(--r-pill)',
                  border: `0.5px solid ${chip.tintBorder}`,
                  background: selectedOutcome === chip.label
                    ? chip.tintBg
                    : 'var(--midnight-surface-2, #14141A)',
                  color: selectedOutcome === chip.label
                    ? chip.tintText
                    : 'var(--midnight-text2, rgba(255,255,255,0.60))',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                  transition: 'all 0.15s ease',
                }}
                onPointerDown={(e) => (e.currentTarget.style.transform = 'scale(0.94)')}
                onPointerUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                onPointerLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}

        {/* Note form (toggle) */}
        {showNoteForm && (
          <div style={{ padding: '4px 16px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Write a quick note..."
              rows={2}
              autoFocus
              style={{
                width: '100%',
                borderRadius: 'var(--r-sm)',
                border: '0.5px solid var(--midnight-border)',
                background: 'var(--midnight-surface-2, #14141A)',
                padding: '8px 10px',
                color: '#FFFFFF',
                // 16px floor — iOS Safari auto-zooms <16px (FND-M-006).
                fontSize: 16,
                lineHeight: 1.4,
                resize: 'none',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-primary"
                disabled={!note.trim() || submittingNote}
                onClick={handleSubmitNote}
                style={{ flex: 1, height: 40, fontSize: 13, padding: '0 14px' }}
              >
                {submittingNote ? 'Saving…' : 'Save note'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNoteForm(false)
                  setNote('')
                }}
                style={{
                  flex: 1,
                  height: 40,
                  borderRadius: 'var(--r-pill)',
                  border: '0.5px solid var(--midnight-border)',
                  background: 'var(--midnight-surface-2, #14141A)',
                  color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* CTA row */}
        {!showNoteForm && (
          <div className="grid grid-cols-3 gap-1.5 px-3 pb-3">
            <CtaButton
              icon={<StickyNote style={{ width: 14, height: 14 }} />}
              label="Add note"
              onClick={() => setShowNoteForm(true)}
            />
            <CtaButton
              icon={<FileText style={{ width: 14, height: 14 }} />}
              label="Create quote"
              onClick={handleCreateQuote}
            />
            <CtaButton
              icon={<CalendarPlus style={{ width: 14, height: 14 }} />}
              label="Follow-up"
              onClick={handleScheduleFollowUp}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function CtaButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        height: 52,
        borderRadius: 'var(--r-sm)',
        border: '0.5px solid var(--midnight-border)',
        background: 'var(--midnight-surface-2, #14141A)',
        color: disabled ? 'var(--midnight-text3, rgba(255,255,255,0.40))' : '#FFFFFF',
        fontSize: 12,
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        touchAction: 'manipulation',
        transition: 'all 0.15s ease',
      }}
      onPointerDown={(e) => {
        if (!disabled) (e.currentTarget.style.background = '#1B1B22')
      }}
      onPointerUp={(e) => {
        (e.currentTarget.style.background = 'var(--midnight-surface-2, #14141A)')
      }}
      onPointerLeave={(e) => {
        (e.currentTarget.style.background = 'var(--midnight-surface-2, #14141A)')
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  )
}

// The /work/:id route matches records by their PREFIXED id (job:x / q:x —
// see parseRecordId in WorkPage); a raw cuid matches nothing and the
// drawer never opens. estimateId is gone: the Estimate model never landed and
// CallSession.estimateId was dropped (FOR-979).
function buildContextInfo(context?: {
  jobId?: string
  quoteId?: string
}): { label: string; route: string | null } | null {
  if (!context) return null
  if (context.jobId) return { label: 'Job', route: `/work/${encodeURIComponent(`job:${context.jobId}`)}` }
  if (context.quoteId) return { label: 'Quote', route: `/work/${encodeURIComponent(`q:${context.quoteId}`)}` }
  return null
}
